import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useFollows, MAX_FOLLOWS } from '../hooks/useFollows';
import { bucketKey } from '../utils/engagementKeys';
import './FollowButton.css';

/**
 * ☆ Follow / ★ Following toggle for a dancer or a studio.
 * Signed-out visitors are sent to the login page.
 */
export default function FollowButton({ type = 'dancer', name, showCount = true }) {
  const navigate = useNavigate();
  const { isFollowing, follow, unfollow, loaded } = useFollows();
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState(null);

  const following = isFollowing(type, name);

  useEffect(() => {
    if (!showCount || !name) return undefined;
    let cancelled = false;
    getDoc(doc(db, 'followerCounts', bucketKey(type, name)))
      .then((snap) => {
        if (!cancelled) setCount(snap.exists() ? Math.max(0, snap.data().count || 0) : 0);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [type, name, showCount]);

  useEffect(() => {
    if (!hint) return undefined;
    const t = setTimeout(() => setHint(null), 7000);
    return () => clearTimeout(t);
  }, [hint]);

  if (!name) return null;

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    const res = following ? await unfollow(type, name) : await follow(type, name);
    setBusy(false);
    if (res.ok) {
      if (!following) {
        setHint({ kind: 'ok' });
        setCount((c) => (c == null ? c : c + 1));
      } else {
        setHint(null);
        setCount((c) => (c == null ? c : Math.max(0, c - 1)));
      }
    } else if (res.reason === 'login') {
      navigate('/login');
    } else if (res.reason === 'limit') {
      setHint({ kind: 'limit' });
    } else {
      setHint({ kind: 'error' });
    }
  };

  return (
    <span className="follow-wrap">
      <button
        type="button"
        className={`follow-btn ${following ? 'following' : ''}`}
        onClick={toggle}
        disabled={busy || !loaded}
        aria-pressed={following}
        title={following ? `Unfollow ${name}` : `Follow ${name} to get updates in My Feed`}
      >
        <span className="follow-star">{following ? '★' : '☆'}</span>
        {following ? 'Following' : 'Follow'}
        {showCount && count > 0 && <span className="follow-count">{count}</span>}
      </button>
      {hint && (
        <span className={`follow-hint ${hint.kind}`}>
          {hint.kind === 'ok' && (
            <>Added to <Link to="/my-feed">My Feed</Link> · <Link to="/notifications">turn on alerts</Link></>
          )}
          {hint.kind === 'limit' && <>You can follow up to {MAX_FOLLOWS} dancers and studios.</>}
          {hint.kind === 'error' && <>Couldn&apos;t update. Please try again.</>}
        </span>
      )}
    </span>
  );
}
